import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useVendorAuthStore } from '../store/vendorAuth.ts';

// Vendor portal client. Tokens travel in the JSON body / Authorization header
// (Bearer), NOT cookies — so withCredentials stays off.
export const vendorApi: AxiosInstance = axios.create({ baseURL: '/api/vendor' });

vendorApi.interceptors.request.use((cfg) => {
  const token = useVendorAuthStore.getState().accessToken;
  if (token && cfg.headers) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Refresh the access token once on 401, then retry; otherwise log out.
async function refreshAccessToken(): Promise<string> {
  const rt = useVendorAuthStore.getState().refreshToken;
  if (!rt) throw new Error('no_refresh_token');
  const r = await axios.post<{ accessToken: string }>('/api/vendor/auth/refresh', { refreshToken: rt });
  const accessToken = r.data.accessToken;
  useVendorAuthStore.setState({ accessToken });
  return accessToken;
}

let refreshing: Promise<string> | null = null;

vendorApi.interceptors.response.use(
  (r) => r,
  async (err: { response?: { status?: number }; config?: InternalAxiosRequestConfig & { _retry?: boolean } }) => {
    const status = err.response?.status;
    const original = err.config;
    if (status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= refreshAccessToken().finally(() => { refreshing = null; });
      try {
        await refreshing;
        return vendorApi(original);
      } catch {
        useVendorAuthStore.getState().logout();
        if (typeof window !== 'undefined') window.location.assign('/vendor/login');
      }
    }
    throw err;
  },
);
